import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components/macro';
import colors from 'Common/constants/colors';
import axios from 'axios';
import { toast } from 'react-toastify';
import notify from 'Common/utils/notify';
import { ArrowBackSharp, CheckSharp } from '@material-ui/icons';
import {
    ButtonsWrapper,
    Layout,
    Title,
    ToolbarButtons,
} from 'Measure/components/vehicle/driver-license/layout';
import Button from 'Common/components/Button';
import LinkButton from 'Common/components/LinkButton';
import Loader from 'Common/components/Loader';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import moment from 'moment-timezone';
import updateDriverLicenseFrontData from 'Measure/api/driver-license/update/updateDriverLicenseFrontData';
import FormField from 'Common/components/form/FormField';
import Select from 'Common/components/form/fields/Select';
import SingleDatePicker from 'Common/components/form/fields/SingleDatePicker';
import Input from 'Common/components/form/fields/Input';
import LicensePreview from 'Measure/components/vehicle/driver-license/create/LicensePreview';
import { Prompt } from 'react-router';
import isDriverLicenseNumberUnique from 'Measure/api/driver-license/update/isDriverLicenseNumberUnique';
import { debounce } from 'lodash';

const Content = styled.div`
    margin-bottom: 10px;
    padding: 20px;
    background: ${colors.DARK_GRAY};
    border-radius: 4px;

    @media screen and (min-width: 940px) {
        display: flex;
        align-items: flex-start;
        justify-content: center;
        margin-bottom: 0;
        padding: 40px;
    }
`;

const Form = styled.form`
    display: block;
    width: 100%;

    @media screen and (min-width: 940px) {
        display: flex;
    }
`;

const LeftColumn = styled.div`
    margin-bottom: 20px;

    @media screen and (min-width: 940px) {
        flex: 2;
        margin-bottom: 0;
        margin-right: 40px;
    }
`;

const RightColumn = styled.div`
    @media screen and (min-width: 940px) {
        flex: 1;
    }
`;

const FieldSet = styled.div`
    margin-bottom: 20px;

    &:last-child {
        margin-bottom: 0;
    }
`;

const FieldSetTitle = styled.div`
    position: relative;
    display: flex;
    align-items: center;
    margin-bottom: 20px;
    color: ${colors.LIGHTEST_GRAY};
    font-size: 18px;
    line-height: 21px;
`;

const FieldSetRow = styled.div`
    @media screen and (min-width: 620px) {
        display: flex;
    }
`;

const FieldSetColumn = styled.div`
    display: flex;

    > div {
        margin-right: 10px;

        &:last-child {
            margin-right: 0;
        }
    }

    @media screen and (min-width: 620px) {
        flex: 1;
    }
`;

const FieldSetLeftColumn = styled(FieldSetColumn)`
    @media screen and (min-width: 620px) {
        margin-right: 20px;
    }
`;

const FieldSetRightColumn = styled(FieldSetColumn)``;

const checkIntervals = [
    {
        value: 1,
        label: 'monatlich',
    },
    {
        value: 3,
        label: 'vierteljährlich',
    },
    {
        value: 6,
        label: 'halbjährlich',
    },
];

const isDueDateOutsideRange = (date, initialDueDate) => {
    // @TODO: Think of a solution on how to let Damir do demos.

    const minDueDate = moment()
        .add(14, 'days')
        .startOf('day');

    return (
        date.isBefore(minDueDate) &&
        (!initialDueDate ||
            !moment(initialDueDate.date)
                .startOf('day')
                .isSame(date.startOf('day')))
    );
};

const isExpiryDateOutsideRange = (date, initialExpiryDate) => {
    const today = moment().endOf('day');

    return (
        date.isBefore(today) &&
        (!initialExpiryDate ||
            !moment(initialExpiryDate.date)
                .startOf('day')
                .isSame(date.startOf('day')))
    );
};

const DriverLicenseFrontDataForm = ({
    driverLicenseControl,
    title,
    confirmButtonText,
    onConfirm,
    onBackButtonClick,
    onDirty,
}) => {
    const [isSaving, setIsSaving] = useState(false);

    const driverLicense = driverLicenseControl.driverLicense;

    const frontImage = driverLicense.files.find(
        file => file.type === 'FRONT_WITH_HOLOGRAM',
    );
    const path = frontImage.file;

    const debouncedUniqueLicenseNumberValidation = useMemo(
        () =>
            debounce(
                async (licenseNumber, validatorContext) =>
                    licenseNumber
                        ? new Promise(async resolve => {
                              try {
                                  const response = await isDriverLicenseNumberUnique(
                                      licenseNumber,
                                      driverLicenseControl.id,
                                  );

                                  const { isUnique, employee } = response.data;

                                  if (isUnique) {
                                      resolve(true);
                                  } else {
                                      resolve(
                                          validatorContext.createError({
                                              message: `Die Führerscheinnummer wird bereits verwendet. In der Kontrolle des Nutzers: ${employee.firstName} ${employee.lastName}.`,
                                              path: validatorContext.path,
                                          }),
                                      );
                                  }
                              } catch (error) {
                                  if (axios.isCancel(error)) {
                                      resolve(true);
                                  } else {
                                      resolve(false);
                                  }
                              }
                          })
                        : true,
                350,
                {
                    leading: true,
                    trailing: true,
                },
            ),
        [driverLicenseControl.id],
    );

    const validationSchema = useMemo(
        () =>
            Yup.object().shape({
                checkIntervalInMonths: Yup.object().required('Pflichtangabe'),
                dueDate: Yup.object()
                    .required('Pflichtangabe')
                    .nullable(),
                fullName: Yup.string().required('Pflichtangabe'),
                address: Yup.string().required('Pflichtangabe'),
                addressNumber: Yup.string().required('Pflichtangabe'),
                residence: Yup.string().required('Pflichtangabe'),
                postCode: Yup.string().required('Pflichtangabe'),
                placeOfBirth: Yup.string().required('Pflichtangabe'),
                dateOfBirth: Yup.object()
                    .required('Pflichtangabe')
                    .nullable(),
                phone: Yup.string().nullable(),
                email: Yup.string().nullable(),
                dateOfIssue: Yup.object()
                    .required('Pflichtangabe')
                    .test(
                        'isBeforeExpiryDate',
                        'Sollte vor dem Ablaufdatum liegen',
                        function(dateOfIssue) {
                            const expiryDateRef = Yup.ref('expiryDate');
                            const expiryDate = this.resolve(expiryDateRef);

                            if (!dateOfIssue) {
                                return true;
                            }

                            return (
                                !expiryDate || dateOfIssue.isBefore(expiryDate)
                            );
                        },
                    )
                    .nullable(),
                expiryDate: Yup.object()
                    .test(
                        'isAfterDateOfIssue',
                        'Sollte nach dem Ausstellungsdatum liegen',
                        function(expiryDate) {
                            const dateOfIssueRef = Yup.ref('dateOfIssue');
                            const dateOfIssue = this.resolve(dateOfIssueRef);

                            if (!expiryDate) {
                                return true;
                            }

                            return (
                                !dateOfIssue || expiryDate.isAfter(dateOfIssue)
                            );
                        },
                    )
                    .nullable(),
                issuedBy: Yup.string().required('Pflichtangabe'),
                licenseNumber: Yup.string()
                    .test(
                        'isUnique',
                        'Ein Fehler ist aufgetreten. Bitte versuche es erneut.',
                        function(licenseNumber) {
                            return debouncedUniqueLicenseNumberValidation(
                                licenseNumber,
                                this,
                            );
                        },
                    )
                    .required('Pflichtangabe'),
            }),
        [debouncedUniqueLicenseNumberValidation],
    );

    const initialValues = useMemo(
        () =>
            driverLicense
                ? {
                      checkIntervalInMonths: driverLicense.checkIntervalInMonths
                          ? checkIntervals.find(
                                option =>
                                    option.value ===
                                    driverLicense.checkIntervalInMonths,
                            )
                          : checkIntervals[0],
                      dueDate: driverLicense.nextCheck?.dueDate
                          ? moment(driverLicense.nextCheck?.dueDate.date)
                          : moment().add(14, 'days'),
                      fullName: driverLicense.employeeData?.fullName ?? '',
                      address: driverLicense.employeeData?.address ?? '',
                      addressNumber:
                          driverLicense.employeeData?.addressNumber ?? '',
                      residence: driverLicense.employeeData?.residence ?? '',
                      postCode: driverLicense.employeeData?.postCode ?? '',
                      placeOfBirth:
                          driverLicense.employeeData?.placeOfBirth ?? '',
                      dateOfBirth: driverLicense.employeeData?.dateOfBirth
                          ? moment(driverLicense.employeeData?.dateOfBirth.date)
                          : null,
                      phone: driverLicense.employeeData?.phone ?? '',
                      email: driverLicense.employeeData?.email ?? '',
                      dateOfIssue: driverLicense.licenseData?.dateOfIssue
                          ? moment(driverLicense.licenseData?.dateOfIssue.date)
                          : null,
                      expiryDate: driverLicense.licenseData?.expiryDate
                          ? moment(driverLicense.licenseData?.expiryDate.date)
                          : null,
                      issuedBy: driverLicense.licenseData?.issuedBy ?? '',
                      licenseNumber:
                          driverLicense.licenseData?.licenseNumber ?? '',
                  }
                : {
                      checkIntervalInMonths: checkIntervals[0],
                      dueDate: moment().add(14, 'days'),
                      fullName: '',
                      address: '',
                      addressNumber: '',
                      residence: '',
                      postCode: '',
                      placeOfBirth: '',
                      dateOfBirth: null,
                      phone: '',
                      email: '',
                      dateOfIssue: null,
                      expiryDate: null,
                      issuedBy: '',
                      licenseNumber: '',
                  },
        [driverLicense],
    );

    const source = useMemo(() => axios.CancelToken.source(), []);

    useEffect(
        () => () => {
            source.cancel();
        },
        [source],
    );

    const {
        errors,
        touched,
        values,
        handleChange,
        setFieldValue,
        setFieldTouched,
        handleBlur,
        handleSubmit,
        isValid,
        dirty,
    } = useFormik({
        initialValues,
        enableReinitialize: true,
        validationSchema,
        onSubmit: async (values, { resetForm }) => {
            setIsSaving(true);

            try {
                await updateDriverLicenseFrontData(
                    driverLicenseControl.id,
                    {
                        ...values,
                        checkIntervalInMonths:
                            values.checkIntervalInMonths.value,
                        dueDate: values.dueDate.toString(),
                        dateOfBirth: values.dateOfBirth.toString(),
                        dateOfIssue: values.dateOfIssue.toString(),
                        expiryDate: values.expiryDate
                            ? values.expiryDate.toString()
                            : null,
                    },
                    source.token,
                );

                await onConfirm();

                resetForm({
                    values,
                });

                setIsSaving(false);
            } catch (error) {
                if (!axios.isCancel(error)) {
                    setIsSaving(false);

                    notify(
                        'Ein Fehler ist aufgetreten. Bitte versuche es erneut.',
                        {
                            type: toast.TYPE.ERROR,
                        },
                    );
                }
            }
        },
    });

    useEffect(() => {
        if (onDirty) {
            onDirty(dirty);
        }
    }, [onDirty, dirty]);

    const minDueDate = moment()
        .add(14, 'days')
        .startOf('day');

    const maxDateOfBirth = moment()
        .subtract(16, 'years')
        .endOf('day');

    const startOfToday = moment().startOf('day');
    const endOfToday = moment().endOf('day');

    return (
        <Layout>
            {!!title && <Title>{title}</Title>}
            <Content>
                <Form onSubmit={handleSubmit}>
                    <LeftColumn>
                        <FieldSet>
                            <FieldSetTitle>
                                Kontrolldaten
                                <LicensePreview path={path} />
                            </FieldSetTitle>
                            <FieldSetRow>
                                <FieldSetLeftColumn>
                                    <FormField>
                                        <Select
                                            label="Intervall"
                                            id="checkIntervalInMonths"
                                            name="checkIntervalInMonths"
                                            error={errors.checkIntervalInMonths}
                                            touched={
                                                touched.checkIntervalInMonths
                                            }
                                            value={values.checkIntervalInMonths}
                                            setFieldValue={setFieldValue}
                                            onBlur={handleBlur}
                                            options={checkIntervals}
                                            isSearchable={false}
                                        />
                                    </FormField>
                                </FieldSetLeftColumn>
                                <FieldSetRightColumn>
                                    <FormField>
                                        <SingleDatePicker
                                            label="Startdatum"
                                            id="dueDate"
                                            name="dueDate"
                                            error={errors.dueDate}
                                            touched={touched.dueDate}
                                            value={values.dueDate}
                                            setFieldValue={setFieldValue}
                                            setFieldTouched={setFieldTouched}
                                            isOutsideRange={date =>
                                                isDueDateOutsideRange(
                                                    date,
                                                    driverLicense.nextCheck
                                                        ?.dueDate,
                                                )
                                            }
                                            initialVisibleMonth={() =>
                                                values.dueDate ?? minDueDate
                                            }
                                            disabled={
                                                driverLicense?.nextCheck
                                                    ?.isInVerificationPeriod
                                            }
                                        />
                                    </FormField>
                                </FieldSetRightColumn>
                            </FieldSetRow>
                        </FieldSet>
                        <FieldSet>
                            <FieldSetTitle>
                                Angaben zur fahrenden Person
                            </FieldSetTitle>
                            <FieldSetRow>
                                <FieldSetLeftColumn>
                                    <FormField>
                                        <Input
                                            label="Vollständiger Name"
                                            id="fullName"
                                            name="fullName"
                                            error={errors.fullName}
                                            touched={touched.fullName}
                                            value={values.fullName}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                        />
                                    </FormField>
                                </FieldSetLeftColumn>
                                <FieldSetRightColumn>
                                    <FormField>
                                        <Input
                                            label="Wohnort"
                                            id="residence"
                                            name="residence"
                                            error={errors.residence}
                                            touched={touched.residence}
                                            value={values.residence}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                        />
                                    </FormField>
                                    <FormField size={95}>
                                        <Input
                                            label="PLZ"
                                            id="postCode"
                                            name="postCode"
                                            error={errors.postCode}
                                            touched={touched.postCode}
                                            value={values.postCode}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                        />
                                    </FormField>
                                </FieldSetRightColumn>
                            </FieldSetRow>
                            <FieldSetRow>
                                <FieldSetLeftColumn>
                                    <FormField>
                                        <Input
                                            label="Straße"
                                            id="address"
                                            name="address"
                                            error={errors.address}
                                            touched={touched.address}
                                            value={values.address}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                        />
                                    </FormField>
                                    <FormField size={95}>
                                        <Input
                                            label="Nr."
                                            id="addressNumber"
                                            name="addressNumber"
                                            error={errors.addressNumber}
                                            touched={touched.addressNumber}
                                            value={values.addressNumber}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                        />
                                    </FormField>
                                </FieldSetLeftColumn>
                                <FieldSetRightColumn>
                                    <FormField>
                                        <Input
                                            label="Geburtsort"
                                            id="placeOfBirth"
                                            name="placeOfBirth"
                                            error={errors.placeOfBirth}
                                            touched={touched.placeOfBirth}
                                            value={values.placeOfBirth}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                        />
                                    </FormField>
                                    <FormField>
                                        <SingleDatePicker
                                            label="Geburtsdatum"
                                            id="dateOfBirth"
                                            name="dateOfBirth"
                                            error={errors.dateOfBirth}
                                            touched={touched.dateOfBirth}
                                            value={values.dateOfBirth}
                                            setFieldValue={setFieldValue}
                                            setFieldTouched={setFieldTouched}
                                            isOutsideRange={date =>
                                                date.isAfter(maxDateOfBirth)
                                            }
                                            initialVisibleMonth={() =>
                                                values.dateOfBirth ??
                                                maxDateOfBirth
                                            }
                                        />
                                    </FormField>
                                </FieldSetRightColumn>
                            </FieldSetRow>
                            <FieldSetRow>
                                <FieldSetLeftColumn>
                                    <FormField>
                                        <Input
                                            label="E-Mail-Addresse"
                                            id="email"
                                            name="email"
                                            error={errors.email}
                                            touched={touched.email}
                                            value={values.email}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            readOnly={
                                                !!driverLicense.employeeData
                                                    ?.email
                                            }
                                        />
                                    </FormField>
                                </FieldSetLeftColumn>
                                <FieldSetRightColumn>
                                    <FormField>
                                        <Input
                                            label="Telefonnummer"
                                            id="phone"
                                            name="phone"
                                            error={errors.phone}
                                            touched={touched.phone}
                                            value={values.phone}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            readOnly={
                                                !!driverLicense.employeeData
                                                    ?.phone
                                            }
                                        />
                                    </FormField>
                                </FieldSetRightColumn>
                            </FieldSetRow>
                        </FieldSet>
                    </LeftColumn>
                    <RightColumn>
                        <FieldSetTitle>Angaben zur Fahrerlaubnis</FieldSetTitle>
                        <div>
                            <FormField>
                                <SingleDatePicker
                                    label="Ausstellungsdatum (Ziff. 4a)"
                                    id="dateOfIssue"
                                    name="dateOfIssue"
                                    error={errors.dateOfIssue}
                                    touched={touched.dateOfIssue}
                                    value={values.dateOfIssue}
                                    setFieldValue={setFieldValue}
                                    setFieldTouched={setFieldTouched}
                                    isOutsideRange={date =>
                                        date.isAfter(endOfToday)
                                    }
                                    initialVisibleMonth={() =>
                                        values.dateOfIssue ?? endOfToday
                                    }
                                />
                            </FormField>
                            <FormField>
                                <SingleDatePicker
                                    label="Ablaufdatum (Ziff. 4b)"
                                    id="expiryDate"
                                    name="expiryDate"
                                    error={errors.expiryDate}
                                    touched={touched.expiryDate}
                                    value={values.expiryDate}
                                    setFieldValue={setFieldValue}
                                    setFieldTouched={setFieldTouched}
                                    isOutsideRange={date =>
                                        isExpiryDateOutsideRange(
                                            date,
                                            driverLicense.licenseData
                                                ?.expiryDate,
                                        )
                                    }
                                    initialVisibleMonth={() =>
                                        values.expiryDate ?? startOfToday
                                    }
                                />
                            </FormField>
                            <FormField>
                                <Input
                                    label="Ausstellungsbehörde (Ziff. 4c)"
                                    id="issuedBy"
                                    name="issuedBy"
                                    error={errors.issuedBy}
                                    touched={touched.issuedBy}
                                    value={values.issuedBy}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                />
                            </FormField>
                            <FormField>
                                <Input
                                    label="Führerscheinnummer (Ziff. 5)"
                                    id="licenseNumber"
                                    name="licenseNumber"
                                    error={errors.licenseNumber}
                                    touched={touched.licenseNumber}
                                    value={values.licenseNumber}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                />
                            </FormField>
                        </div>
                    </RightColumn>
                </Form>
            </Content>
            <ToolbarButtons>
                <ButtonsWrapper>
                    <Button
                        disabled={isSaving || !isValid}
                        onClick={handleSubmit}
                    >
                        {isSaving ? <Loader size={24} /> : <CheckSharp />}
                        {confirmButtonText}
                    </Button>
                </ButtonsWrapper>
                <ButtonsWrapper>
                    {!!onBackButtonClick && (
                        <Button
                            icon={ArrowBackSharp}
                            onClick={onBackButtonClick}
                        >
                            Zurück
                        </Button>
                    )}
                    <LinkButton
                        disabled={isSaving}
                        to="/measures/vehicles/driver-licenses"
                    >
                        Abbrechen
                    </LinkButton>
                </ButtonsWrapper>
            </ToolbarButtons>
            <Prompt
                when={dirty}
                message="Möchten Sie die Seite wirklich verlassen? Nicht gespeicherte Änderungen gehen verloren."
            />
        </Layout>
    );
};

DriverLicenseFrontDataForm.defaultProps = {
    title: null,
    confirmButtonText: 'Speichern',
    onBackButtonClick: null,
    onDirty: null,
};

DriverLicenseFrontDataForm.propTypes = {
    driverLicenseControl: PropTypes.object.isRequired,
    title: PropTypes.string,
    confirmButtonText: PropTypes.string,
    onConfirm: PropTypes.func.isRequired,
    onBackButtonClick: PropTypes.func,
    onDirty: PropTypes.func,
};

export default DriverLicenseFrontDataForm;
